import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  type NormalizedStaffWorkout,
  type StaffProgrammingData,
  StaffProgrammingExperience,
  isAffiliateOwnedWorkout,
} from '@/components/programming/StaffProgrammingExperience';
import { WorkoutForm, type WorkoutFormData } from '@/components/programming/WorkoutForm';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { useApi } from '@/hooks/useApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { type FieldErrors, getFieldError } from '@/lib/validationErrors';
import { ApiError, toApiError } from '@/services/api';
import { useEffectiveRole } from '@/stores/authStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

export default function AffiliateProgramming() {
  return (
    <DashboardLayout>
      <AffiliateProgrammingContent />
    </DashboardLayout>
  );
}

function AffiliateProgrammingContent() {
  const affiliatesApi = useAffiliatesApi();
  const { api } = useApi();
  const queryClient = useQueryClient();
  const effectiveRole = useEffectiveRole();
  const { hasPermission, hasCoachFlag } = usePermissions();
  const isOwner = effectiveRole === 'affiliate_owner';
  const canCreateWorkout = isOwner || hasCoachFlag('can_create_workouts');
  const canLogScores = hasPermission('log_scores');

  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState<NormalizedStaffWorkout | null>(null);
  const [workoutErrors, setWorkoutErrors] = useState<FieldErrors>();
  const createWorkoutSubmission = useSubmissionLock<{ formData: WorkoutFormData }>();
  const overrideWorkoutSubmission = useSubmissionLock<{
    workoutId: string;
    formData: WorkoutFormData;
  }>();

  const resetWorkoutForm = () => {
    setEditingWorkout(null);
    setWorkoutErrors(undefined);
  };

  const openCreateForm = () => {
    createWorkoutSubmission.reset();
    overrideWorkoutSubmission.reset();
    resetWorkoutForm();
    setShowWorkoutForm(true);
  };

  const openEditForm = (workout: NormalizedStaffWorkout) => {
    createWorkoutSubmission.reset();
    overrideWorkoutSubmission.reset();
    setEditingWorkout(workout);
    setWorkoutErrors(undefined);
    setShowWorkoutForm(true);
  };

  const createWorkoutMutation = useMutation({
    mutationFn: async ({
      formData,
      idempotencyKey,
    }: { formData: WorkoutFormData; idempotencyKey: string }) => {
      const response = await affiliatesApi.createWorkout(
        {
          name: formData.name,
          description: formData.description || undefined,
          scheduled_date: formData.scheduled_date,
          scoring_type: formData.scoring_type,
          scoring_direction: formData.scoring_direction,
          movements: formData.movements || undefined,
          equipment: formData.equipment || undefined,
          workout_type: formData.track_id || 'daily_wod',
          publish_to_gym: formData.publish_to_gym ?? true,
          shared_to_global: formData.shared_to_global ?? false,
          is_favorite: formData.is_favorite ?? false,
        },
        idempotencyKey
      );
      if (!response.success) throw toApiError(response, 'Failed to create workout');
      return response.data;
    },
    onSuccess: () => {
      createWorkoutSubmission.reset();
      setShowWorkoutForm(false);
      resetWorkoutForm();
      setWorkoutErrors(undefined);
      queryClient.invalidateQueries({ queryKey: ['staff-programming', 'affiliate'] });
    },
    onError: (error) => {
      createWorkoutSubmission.release();
      if (error instanceof ApiError && hasWorkoutFieldError(error.details)) {
        setWorkoutErrors(error.details);
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Failed to create workout');
    },
  });

  const overrideWorkoutMutation = useMutation({
    mutationFn: async ({
      workoutId,
      formData,
      idempotencyKey,
    }: {
      workoutId: string;
      formData: WorkoutFormData;
      idempotencyKey: string;
    }) => {
      const response = await affiliatesApi.createWorkoutOverride(
        workoutId,
        {
          name: formData.name || undefined,
          description: formData.description || undefined,
          movements: formData.movements || undefined,
        },
        idempotencyKey
      );
      if (!response.success) throw toApiError(response, 'Failed to save workout');
      return response.data;
    },
    onSuccess: () => {
      overrideWorkoutSubmission.reset();
      setShowWorkoutForm(false);
      resetWorkoutForm();
      setWorkoutErrors(undefined);
      queryClient.invalidateQueries({ queryKey: ['staff-programming', 'affiliate'] });
    },
    onError: (error) => {
      overrideWorkoutSubmission.release();
      if (error instanceof ApiError && hasWorkoutFieldError(error.details)) {
        setWorkoutErrors(error.details);
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Failed to save workout');
    },
  });

  const updateWorkoutMutation = useMutation({
    mutationFn: async ({
      workoutId,
      formData,
      idempotencyKey,
    }: {
      workoutId: string;
      formData: WorkoutFormData;
      idempotencyKey: string;
    }) => {
      const response = await affiliatesApi.updateWorkout(
        workoutId,
        {
          name: formData.name,
          description: formData.description || undefined,
          scheduled_date: formData.scheduled_date,
          scoring_type: formData.scoring_type,
          scoring_direction: formData.scoring_direction,
          movements: formData.movements || undefined,
          equipment: formData.equipment || undefined,
          is_favorite: formData.is_favorite,
        },
        idempotencyKey
      );
      if (!response.success) throw toApiError(response, 'Failed to update workout');
      return response.data;
    },
    onSuccess: () => {
      createWorkoutSubmission.reset();
      setShowWorkoutForm(false);
      resetWorkoutForm();
      queryClient.invalidateQueries({ queryKey: ['staff-programming', 'affiliate'] });
    },
    onError: (error) => {
      createWorkoutSubmission.release();
      if (error instanceof ApiError && hasWorkoutFieldError(error.details)) {
        setWorkoutErrors(error.details);
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Failed to update workout');
    },
  });

  const isWorkoutFormPending =
    createWorkoutSubmission.isLocked || overrideWorkoutSubmission.isLocked;
  const isEditingAffiliateWorkout = !!editingWorkout && isAffiliateOwnedWorkout(editingWorkout);

  return (
    <>
      <StaffProgrammingExperience
        title="Programming"
        description="View scheduled workouts, switch programme sections, and launch TV display mode."
        queryKey={['staff-programming', 'affiliate']}
        primaryAction={
          canCreateWorkout
            ? {
                label: 'Create Workout',
                icon: 'plus',
                onClick: openCreateForm,
              }
            : undefined
        }
        canLogScores={canLogScores}
        canEditWorkouts={isOwner}
        showSpecialty={false}
        showFavoriteFilter
        canEditWorkout={(workout) => !isAffiliateOwnedWorkout(workout) || workout.is_creator}
        onEditWorkout={openEditForm}
        onScoreLogged={() => queryClient.invalidateQueries({ queryKey: ['staff-programming'] })}
        fetchMonth={async ({ year, month }): Promise<StaffProgrammingData> => {
          const response = await affiliatesApi.getProgramming({
            year,
            month: String(month).padStart(2, '0'),
          });
          if (!response.success) throw new Error(response.error);
          return {
            workouts: response.data?.workouts || [],
            tracks: response.data?.tracks || [],
          };
        }}
        onToggleFavorite={async (workout) => {
          const response = await api.post(`/programming/workouts/${workout.id}/favorite`, {
            is_favorite: !workout.is_favorite,
          });
          if (!response.success) {
            throw new Error(response.error || 'Failed to update Favourite');
          }
          await queryClient.invalidateQueries({ queryKey: ['staff-programming', 'affiliate'] });
        }}
      />

      {showWorkoutForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <WorkoutForm
              title={
                editingWorkout
                  ? isEditingAffiliateWorkout
                    ? 'Edit Workout'
                    : 'Override HQ Workout'
                  : 'Create Workout'
              }
              subtitle={
                editingWorkout
                  ? isEditingAffiliateWorkout
                    ? 'Update this custom workout.'
                    : 'Create an affiliate-specific version of this HQ workout.'
                  : isOwner
                    ? 'Choose where to save this workout, then add its details.'
                    : 'Save this workout to My Workouts, with the option to add it to Community Workouts.'
              }
              isSubmitting={isWorkoutFormPending}
              submitLabel={
                editingWorkout
                  ? isEditingAffiliateWorkout
                    ? 'Update Workout'
                    : 'Save Override'
                  : 'Create Workout'
              }
              initialValues={
                editingWorkout
                  ? {
                      name: editingWorkout.name,
                      description: editingWorkout.description || '',
                      movements: editingWorkout.movements.join('\n'),
                      scoring_type: editingWorkout.scoring_type || 'time',
                      scoring_direction: editingWorkout.scoring_direction || 'ascending',
                      equipment: editingWorkout.equipment.join(', '),
                      scheduled_date: editingWorkout.scheduled_date || '',
                    }
                  : undefined
              }
              visibilityMode={editingWorkout ? 'none' : isOwner ? 'owner' : 'personal'}
              showFavorite={!editingWorkout}
              fieldErrors={workoutErrors}
              onSubmit={(formData) => {
                setWorkoutErrors(undefined);
                if (editingWorkout && !isEditingAffiliateWorkout) {
                  const attempt = overrideWorkoutSubmission.begin({
                    workoutId: editingWorkout.id,
                    formData,
                  });
                  if (!attempt) return;
                  overrideWorkoutMutation.mutate({
                    ...attempt.payload,
                    idempotencyKey: attempt.idempotencyKey,
                  });
                } else if (editingWorkout) {
                  const attempt = createWorkoutSubmission.begin({ formData });
                  if (!attempt) return;
                  updateWorkoutMutation.mutate({
                    workoutId: editingWorkout.id,
                    ...attempt.payload,
                    idempotencyKey: attempt.idempotencyKey,
                  });
                } else {
                  const attempt = createWorkoutSubmission.begin({ formData });
                  if (!attempt) return;
                  createWorkoutMutation.mutate({
                    ...attempt.payload,
                    idempotencyKey: attempt.idempotencyKey,
                  });
                }
              }}
              onCancel={() => {
                createWorkoutSubmission.reset();
                overrideWorkoutSubmission.reset();
                setShowWorkoutForm(false);
                resetWorkoutForm();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function hasWorkoutFieldError(errors: FieldErrors | undefined) {
  return ['name', 'scheduled_date', 'description'].some((field) => getFieldError(errors, field));
}
